import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { EditSongIdeasModalProps, SongIdea } from '../../types/planningTypes';
import apiClient from '../../../../utils/apiClient';

const EditSongIdeasModal: React.FC<EditSongIdeasModalProps> = ({
  show,
  onHide,
  serviceId,
  songIdeas,
  onUpdate
}) => {
  const [editedIdeas, setEditedIdeas] = useState<SongIdea[]>([]);
  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (show) {
      setEditedIdeas(songIdeas.length > 0 ? [...songIdeas] : []);
      setError('');
    }
  }, [show, songIdeas]);

  const handleAddIdea = () => {
    setEditedIdeas([...editedIdeas, { title: '', composer: '', lyricist: '', tune: '' }]);
  };

  const handleRemoveIdea = (index: number) => {
    const newIdeas = editedIdeas.filter((_, i) => i !== index);
    setEditedIdeas(newIdeas);
  };

  const handleIdeaChange = (index: number, field: keyof SongIdea, value: string) => {
    const newIdeas = [...editedIdeas];
    newIdeas[index] = { ...newIdeas[index], [field]: value };
    setEditedIdeas(newIdeas);
  };

  const handleSubmit = async () => {
    setError('');

    // Validate: at least one idea must have a title
    const validIdeas = editedIdeas.filter(idea => idea.title.trim() !== '');

    // Clean up optional fields
    const cleanedIdeas = validIdeas.map(idea => ({
      title: idea.title.trim(),
      composer: idea.composer?.trim() || undefined,
      lyricist: idea.lyricist?.trim() || undefined,
      tune: idea.tune?.trim() || undefined
    }));

    setIsSubmitting(true);

    try {
      const response = await apiClient.put(
        `/readings/${serviceId}/song-ideas`,
        { song_ideas: cleanedIdeas }
      );

      onUpdate(response.data);
    } catch (err: any) {
      console.error('Error updating song ideas:', err);
      setError(err.response?.data?.message || 'Failed to update song ideas');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Edit Song Ideas</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        <div className="song-ideas-form">
          {editedIdeas.map((idea, index) => (
            <div key={index} className="mb-3 p-3 border rounded position-relative">
              <Button
                variant="link"
                size="sm"
                className="position-absolute top-0 end-0 text-danger"
                onClick={() => handleRemoveIdea(index)}
                title="Remove this idea"
              >
                <i className="bi bi-trash"></i>
              </Button>

              <Form.Group className="mb-2">
                <Form.Label className="fw-bold">
                  Title <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={idea.title}
                  onChange={(e) => handleIdeaChange(index, 'title', e.target.value)}
                  placeholder="Song title"
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>Composer</Form.Label>
                <Form.Control
                  type="text"
                  value={idea.composer || ''}
                  onChange={(e) => handleIdeaChange(index, 'composer', e.target.value)}
                  placeholder="Composer name (optional)"
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>Lyricist</Form.Label>
                <Form.Control
                  type="text"
                  value={idea.lyricist || ''}
                  onChange={(e) => handleIdeaChange(index, 'lyricist', e.target.value)}
                  placeholder="Lyricist name (optional)"
                />
              </Form.Group>

              <Form.Group className="mb-0">
                <Form.Label>Tune</Form.Label>
                <Form.Control
                  type="text"
                  value={idea.tune || ''}
                  onChange={(e) => handleIdeaChange(index, 'tune', e.target.value)}
                  placeholder="Tune name (optional)"
                />
              </Form.Group>
            </div>
          ))}

          <Button variant="outline-primary" onClick={handleAddIdea} className="w-100">
            <i className="bi bi-plus-circle me-2"></i>
            Add Song Idea
          </Button>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditSongIdeasModal;
